import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
} from 'react-bootstrap';
import { DataTable, Filter, Input, PageWrapper, Select } from '../../components';
import { useForm, FormProvider } from "react-hook-form";
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../../shared/enums';
import { useTranslation } from 'react-i18next';
import { getWithdrawalList, updateStatus } from '../../api/withdrawal';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { DATETIME_FORMAT } from '../../shared/constants';

const Withdrawal = ({ role }) => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const form = useForm()
    const [filter, setFilter] = useState({});
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [totalPages, setTotalPage] = useState(0);
    const [data, setData] = useState([]);

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filter changes
    };

    const showApprovalModal = (selected) => {
        form.reset(selected);
        setSelectedWithdrawal(selected);
    }

    const columns = [
        { key: "createdAt", label: t("CREATED_AT"), render: (createdAt, data) => dayjs(createdAt).format(DATETIME_FORMAT) },
        { key: "fullName", label: t("FULL_NAME"), render: (fullName, data) => data?.user?.fullName },
        { key: "phoneNumber", label: t("PHONE_NUMBER"), render: (phoneNumber, data) => data?.user?.countryCode + data?.user?.phoneNumber },
        { key: "currency", label: t("CURRENCY") },
        { key: "amount", label: t("AMOUNT"), render: (amount, data) => amount?.toFixed(2) },
        { key: "status", label: t("STATUS") },
        { key: "statusDesc", label: t("REMARK") },
        {
            key: "action", label: t("ACTION"), render: (v, data) => <Dropdown>
                <Dropdown.Toggle variant="primary" size="sm" className='py-0'></Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => navigate(data?.driverId ? Pages.DriverWallet.replace(":id", data.driverId) : Pages.UserWallet.replace(":id", data.user.id))}>{t("VIEW_PROFILE")}</Dropdown.Item>
                    {['succeed', 'failed'].includes(data.status) ? <></> : <Dropdown.Item onClick={() => showApprovalModal(data)}>{t("APPROVAL")}</Dropdown.Item>}
                </Dropdown.Menu>
            </Dropdown>
        },
    ]

    const filters = [
        { type: "input", label: t("SEARCH"), name: "search" },
        { type: "datetime", label: t("FORM_DATE"), name: "fromDate" },
        { type: "datetime", label: t("TO_DATE"), name: "toDate" },
        {
            type: "select", label: t("STATUS"), name: "status", options: [
                { label: "All", value: "" },
                { label: t("Pending"), value: 'pending' },
                { label: t("Processing"), value: 'processing' },
                { label: t("Succeed"), value: 'succeed' },
                { label: t("Failed"), value: 'failed' },
            ]
        }
    ]

    useEffect(() => {
        getData(pagination, filter)
    }, [pagination, filter])

    const getData = async (pagination, filter) => {
        const response = await getWithdrawalList({ skip: (pagination?.page - 1) * pagination?.pageSize, limit: pagination?.pageSize, role, ...filter })
        setData(response.items)
        setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize))
    }

    const update = async (data) => {
        const response = await updateStatus(data.id, { status: data.status, statusDesc: data.statusDesc })
        if (response.success) {
            setSelectedWithdrawal(null)
            getData(pagination)
        }
    }

    const getExportData = () => data.map(item => ({
        [t("FULL_NAME")]: item?.user?.fullName,
        [t("PHONE_NUMBER")]: item?.user?.countryCode + item?.user?.phoneNumber,
        [t("CURRENCY")]: item.currency,
        [t("AMOUNT")]: item.amount?.toFixed(2),
        [t("STATUS")]: item.status,
        [t("REMARK")]: item.statusDesc,
        [t("CREATED_AT")]: dayjs(item.createdAt).format(DATETIME_FORMAT)
    }));

    const exportToCsv = () => {
        const worksheet = XLSX.utils.json_to_sheet(getExportData());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `withdrawals_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    }

    return (
        <PageWrapper headerProps={{ title: "WITHDRAWAL" }}>
            <Filter
                filters={filters}
                onChangeFilter={handleFilterChange}
                onExport={exportToCsv}
                exportDisabled={data.length === 0}
            />
            <DataTable columns={columns} data={data} pagination={{ ...pagination, totalPages, onChange: setPagination }} />

            <Modal show={selectedWithdrawal} onHide={() => setSelectedWithdrawal(null)}>
                <FormProvider {...form}>
                    <Form onSubmit={form.handleSubmit(update)}>
                        <Modal.Header closeButton>
                            <Modal.Title>{t("APPROVAL")}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ height: "50vh", overflow: "auto" }}>
                            <Select
                                label={t("STATUS")}
                                name={"status"}
                                rules={{ required: t("STATUS_IS_REQUIRED") }}
                                options={[
                                    { label: t("Pending"), value: 'pending' },
                                    { label: t("Processing"), value: 'processing' },
                                    { label: t("Succeed"), value: 'succeed' },
                                    { label: t("Failed"), value: 'failed' },
                                ]}
                            />
                            <Input label={t("REMARK")} name={"statusDesc"} rows={3} />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setSelectedWithdrawal(null)}>
                                {t('CLOSE')}
                            </Button>
                            <Button variant="primary" type='submit'>
                                {t('SUBMIT')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </FormProvider>
            </Modal>
        </PageWrapper>
    );
};

export default Withdrawal;
